import type * as THREE from "three";

/**
 * MAP WEATHERING (2026-10-23, `graphics` realism pass 2) — a world-space
 * fragment term on the merged map-surface materials (renderer.ts `familyMat`
 * and the trim `detailMat`). It fixes the three things that still read as CG
 * on every crafted map, including the ones whose trim budget is exhausted
 * (medieval_castle, wild_west_train_pursuit, neo_island, rooftop_spiderweb), because it
 * costs no geometry at all:
 *
 *  1. TILING. Every surface texture repeats every 3–6 m. The canvases carry
 *     their own blooms, but only WITHIN a tile, so a 60 m grass field or plaza
 *     shows the same patch in a grid. A two-octave world-space value noise
 *     (~22 m and ~5 m) modulates albedo by a few percent, so no two repeats
 *     look alike and joined slabs stay continuous (it is world-anchored).
 *  2. CLEAN WALLS. Real walls carry rain/grime streaks. Vertical faces get
 *     narrow, long, vertically-stretched dark streaks, gated by a coarse
 *     patch noise so they cluster in weathered zones instead of striping the
 *     whole map evenly. Darken-only.
 *  3. DAMP GROUND. Up-facing made ground gets soft damp patches: a little
 *     darker AND a little smoother (lower roughness), which is how wet stone
 *     actually differs from dry — a sheen change, not just a colour change.
 *  4. REPEATING LIT WINDOWS (facade only). The facade glow canvas lights the
 *     same 40 % of a 4 x 4 window grid in every tile, so at night every
 *     building showed the identical lit pattern on a 4.5 m period. Each window
 *     cell now gets its own hashed brightness (some go dark), seeded by the
 *     window index AND the face's plane coordinate so neighbouring buildings
 *     differ. Only ever DIMS the emissive (factor <= 1): no bloom blow-out.
 *
 * Safety rails (asserted by bench-architecture section J):
 *  - the final albedo factor is CLAMPED to [K_MIN, K_MAX]; the positive side of
 *    the macro term is halved, so the map can only brighten by a few percent
 *    and never past what the vertex tint x texture already allowed;
 *  - roughness only ever goes DOWN by at most (1 - R_MIN), never up;
 *  - window emissive factor is in [0, 1];
 *  - ONE program key (`npWeather1`) for every weathered material: the presets
 *    are per-material UNIFORMS, not defines, so this adds no shader variants
 *    beyond one per already-distinct base material, and all of them are map
 *    materials compiled under the load screen (renderer.precompile ->
 *    compileAsync on the scene). A same-map round rebuild re-hits the cache.
 *  - visual only, nothing in packages/shared; no per-frame work at all (the
 *    uniforms are written once, at material build);
 *  - the caller gates it OFF on lowSpec / TV / weak (integrated) GPUs, so those
 *    machines compile and draw exactly the shader they did before.
 */

/** Per-material strengths. All darken-biased; see the clamps below. */
export interface WeatherPreset {
  /** +/- albedo swing of the world-space macro variation (positive side halved). */
  macro: number;
  /** max darkening of a full-strength rain streak on a vertical face. */
  streak: number;
  /** max darkening of a damp patch on an up-facing surface. */
  damp: number;
  /** max roughness REDUCTION in a damp patch (wet sheen). 0 = natural ground. */
  wet: number;
  /** 1 = per-window lit variation on the emissive facade glow. */
  windows: number;
}

/** Albedo factor clamp — the brightness guarantee. */
export const WEATHER_K_MIN = 0.72;
export const WEATHER_K_MAX = 1.05;
/** Roughness factor floor — wet patches never go glossier than 0.6 x. */
export const WEATHER_R_MIN = 0.6;
/** Facade window: fraction of windows turned dark, and the lit range. */
export const WINDOW_DARK = 0.14;
export const WINDOW_MIN = 0.55;

export type WeatherKind =
  | "terrain" | "ground" | "paved" | "roof"
  | "facade" | "plaster" | "wood" | "metal" | "trim" | "timberTrim";

export const WEATHER_PRESETS: Readonly<Record<WeatherKind, Readonly<WeatherPreset>>> = {
  // natural ground (day grass/dirt/sand): the field-scale tiling is the tell;
  // damp hollows darken but grass does not turn glossy
  terrain: { macro: 0.10, streak: 0, damp: 0.08, wet: 0, windows: 0 },
  // night-theme concrete ground
  ground: { macro: 0.09, streak: 0, damp: 0.10, wet: 0.22, windows: 0 },
  paved: { macro: 0.08, streak: 0, damp: 0.12, wet: 0.28, windows: 0 },
  roof: { macro: 0.08, streak: 0.10, damp: 0.08, wet: 0.15, windows: 0 },
  facade: { macro: 0.05, streak: 0.14, damp: 0, wet: 0, windows: 1 },
  plaster: { macro: 0.08, streak: 0.20, damp: 0.05, wet: 0, windows: 0 },
  wood: { macro: 0.07, streak: 0.10, damp: 0.05, wet: 0, windows: 0 },
  metal: { macro: 0.06, streak: 0.16, damp: 0.05, wet: 0.1, windows: 0 },
  // the trim canvas already paints weather drips below its joints
  trim: { macro: 0.06, streak: 0.12, damp: 0.05, wet: 0.1, windows: 0 },
  timberTrim: { macro: 0.05, streak: 0.08, damp: 0, wet: 0, windows: 0 },
};

/** The map-surface family -> preset routing. `null` = leave untouched
 *  (Minecraft imports keep their crisp voxel look; glass is glass). */
export function weatherKindFor(surf: string): WeatherKind | null {
  switch (surf) {
    case "terrain": return "terrain";
    case "concrete": return "ground";
    case "paving": return "paved";
    case "roofing": return "roof";
    case "facade": return "facade";
    case "plaster": return "plaster";
    case "wood": return "wood";
    case "surface": return "metal";
    case "trim": return "trim";
    default: return null;
  }
}

export const WEATHER_PROGRAM_KEY = "npWeather1";

const f = (n: number): string => (Number.isInteger(n) ? `${n}.0` : `${n}`);

/** Shared GLSL helpers (fragment). Hoskins hash: no sin(), stable under highp
 *  at map-scale coordinates. */
const FRAG_DECL = /* glsl */ `
uniform float uNpMacro;
uniform float uNpStreak;
uniform float uNpDamp;
uniform float uNpWet;
uniform float uNpWin;
varying vec3 vNpW;
varying vec3 vNpN;
flat in float vNpSeed;
float npHash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float npNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 fr = fract(p);
  vec2 u = fr * fr * (3.0 - 2.0 * fr);
  return mix(mix(npHash(i), npHash(i + vec2(1.0, 0.0)), u.x),
             mix(npHash(i + vec2(0.0, 1.0)), npHash(i + vec2(1.0, 1.0)), u.x), u.y);
}
`;

/** After `color_fragment` (vertex tint already applied). Declares npDamp for
 *  the roughness stage below. */
const FRAG_ALBEDO = /* glsl */ `
float npDamp = 0.0;
{
  vec3 npA = abs(normalize(vNpN));
  float npUp = smoothstep(0.55, 0.8, npA.y);
  // horizontal coordinate ALONG a vertical face (continuous down a wall run)
  float npH = npA.x > npA.z ? vNpW.z : vNpW.x;
  vec2 npP = mix(vec2(npH, vNpW.y), vNpW.xz, npUp);
  float npM = npNoise(npP * 0.045) * 0.62 + npNoise(npP * 0.19 + 17.3) * 0.38;
  float npD = (npM - 0.5) * 2.0;
  float npK = 1.0 + uNpMacro * (npD < 0.0 ? npD : 0.5 * npD);
  if (uNpStreak > 0.0 && npUp < 1.0) {
    float npS = smoothstep(0.58, 0.86, npNoise(vec2(npH * 2.4, vNpW.y * 0.11)));
    float npZ = smoothstep(0.38, 0.72, npNoise(vec2(npH * 0.21 + 5.1, vNpW.y * 0.06)));
    npK *= 1.0 - uNpStreak * npS * npZ * (1.0 - npUp);
  }
  npDamp = npUp * smoothstep(0.52, 0.78, npNoise(vNpW.xz * 0.11 + 31.7));
  npK *= 1.0 - uNpDamp * npDamp;
  diffuseColor.rgb *= clamp(npK, ${f(WEATHER_K_MIN)}, ${f(WEATHER_K_MAX)});
}
`;

const FRAG_ROUGH = /* glsl */ `
roughnessFactor *= clamp(1.0 - uNpWet * npDamp, ${f(WEATHER_R_MIN)}, 1.0);
`;

/** After `emissivemap_fragment`. The facade canvas is a 4 x 4 window grid per
 *  UV repeat, so floor(uv * 4) is a unique window index across the face. */
const FRAG_EMISSIVE = /* glsl */ `
#ifdef USE_EMISSIVEMAP
if (uNpWin > 0.0) {
  vec2 npC = floor(vEmissiveMapUv * 4.0);
  float npW = npHash(npC + vec2(fract(vNpSeed * 0.1373) * 97.0, fract(vNpSeed * 0.0917) * 61.0));
  float npLit = npW < ${f(WINDOW_DARK)} ? 0.0 : ${f(WINDOW_MIN)} + ${f(1 - WINDOW_MIN)} * fract(npW * 7.13);
  totalEmissiveRadiance *= mix(1.0, npLit, uNpWin);
}
#endif
`;

const VERT_DECL = /* glsl */ `
varying vec3 vNpW;
varying vec3 vNpN;
flat out float vNpSeed;
`;

/** After `begin_vertex` (objectNormal is in scope from beginnormal_vertex).
 *  vNpSeed = the world coordinate along the face's dominant axis — constant
 *  across a flat face, so `flat` gives every triangle of it the SAME exact
 *  value (no interpolation jitter flickering a window across a hash edge). */
const VERT_BODY = /* glsl */ `
{
  vec4 npW4 = modelMatrix * vec4(transformed, 1.0);
  vNpW = npW4.xyz;
  vec3 npN = normalize(mat3(modelMatrix) * objectNormal);
  vNpN = npN;
  vec3 npA = abs(npN);
  vNpSeed = (npA.x >= npA.y && npA.x >= npA.z) ? npW4.x : (npA.y >= npA.z ? npW4.y : npW4.z);
}
`;

/** Anchor -> injected text. Exported so the bench can assert every anchor is
 *  present exactly once in three's real shader source (a missing anchor makes
 *  String.replace a SILENT no-op — or worse, half-applies the patch). */
export const WEATHER_ANCHORS = {
  vertex: [
    ["#include <common>", VERT_DECL],
    ["#include <begin_vertex>", VERT_BODY],
  ],
  fragment: [
    ["#include <common>", FRAG_DECL],
    ["#include <color_fragment>", FRAG_ALBEDO],
    ["#include <roughnessmap_fragment>", FRAG_ROUGH],
    ["#include <emissivemap_fragment>", FRAG_EMISSIVE],
  ],
} as const;

/** Pure string patch (no GL) — the part the bench exercises. Returns null and
 *  leaves the shader untouched if ANY anchor is missing, so a future three
 *  upgrade degrades to the stock look instead of a compile error. */
export function patchWeatherShader(vs: string, fs: string): { vs: string; fs: string } | null {
  for (const [a] of WEATHER_ANCHORS.vertex) if (vs.split(a).length !== 2) return null;
  for (const [a] of WEATHER_ANCHORS.fragment) if (fs.split(a).length !== 2) return null;
  for (const [a, add] of WEATHER_ANCHORS.vertex) vs = vs.replace(a, `${a}\n${add}`);
  for (const [a, add] of WEATHER_ANCHORS.fragment) fs = fs.replace(a, `${a}\n${add}`);
  return { vs, fs };
}

/** Attach the weathering to a map material. Uniform objects are created once
 *  here (map-load time), never per frame. */
export function applyMapWeather<T extends THREE.Material>(mat: T, preset: Readonly<WeatherPreset>): T {
  const uniforms = {
    uNpMacro: { value: preset.macro },
    uNpStreak: { value: preset.streak },
    uNpDamp: { value: preset.damp },
    uNpWet: { value: preset.wet },
    uNpWin: { value: preset.windows },
  };
  mat.onBeforeCompile = (shader) => {
    const p = patchWeatherShader(shader.vertexShader, shader.fragmentShader);
    if (!p) return;
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = p.vs;
    shader.fragmentShader = p.fs;
  };
  // ONE key for every weathered material: three otherwise keys on the
  // closure's source text, which is identical anyway, but an explicit constant
  // documents that the presets are uniforms and can never fork the program.
  mat.customProgramCacheKey = () => WEATHER_PROGRAM_KEY;
  return mat;
}
